import { supabase } from '@/integrations/supabase/client';
import { LeadScore, LeadScoreBreakdown } from '@/types/leadScore';
import { calculateAndSaveLeadScore } from './leadScoreCalculator';
import { getLeadById } from '../queries/getLeadById';
import { getActivitiesByLead } from '../activityService';
import { calculateScoreBreakdown } from './componentScores';

/**
 * Optimized aggregated function to fetch all lead score data in parallel
 * Reduces 3 sequential queries to 2 parallel queries
 */
export const getLeadScoreComplete = async (leadId: string): Promise<{
  score: LeadScore | null;
  breakdown: LeadScoreBreakdown | null;
  history: any[];
}> => {
  try {
    // Fetch score and history in parallel
    const [scoreResult, historyResult] = await Promise.all([
      supabase
        .from('lead_scores')
        .select('*')
        .eq('lead_id', leadId)
        .order('last_calculated', { ascending: false })
        .limit(1)
        .maybeSingle(),
      supabase
        .from('lead_score_history')
        .select('*')
        .eq('lead_id', leadId)
        .order('calculated_at', { ascending: true })
    ]);

    // Process score
    let score: LeadScore | null = null;
    if (scoreResult.error) {
      console.error('Error fetching lead score:', scoreResult.error);
    } else if (!scoreResult.data) {
      // If no score exists, calculate it
      score = await calculateAndSaveLeadScore(leadId);
    } else {
      const data = scoreResult.data;
      score = {
        id: data.id,
        leadId: data.lead_id,
        totalScore: data.total_score,
        demographicScore: data.demographic_score,
        engagementScore: data.engagement_score,
        activityScore: data.activity_score,
        recencyScore: data.recency_score,
        lastCalculated: data.last_calculated,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      };
    }

    // Process history
    const history = historyResult.error 
      ? []
      : historyResult.data.map(record => ({
          date: record.calculated_at.split('T')[0],
          totalScore: record.total_score,
          demographic: record.demographic_score,
          engagement: record.engagement_score,
          activity: record.activity_score,
          recency: record.recency_score
        }));

    // Calculate breakdown from existing score data if available
    let breakdown: LeadScoreBreakdown | null = null;
    if (score) {
      // Fetch lead and activities to calculate breakdown
      const [lead, activities] = await Promise.all([
        getLeadById(leadId),
        getActivitiesByLead(leadId)
      ]);
      
      if (lead) {
        breakdown = calculateScoreBreakdown(lead, activities);
      }
    }

    return { score, breakdown, history };
  } catch (error) {
    console.error('Error in getLeadScoreComplete:', error);
    return { score: null, breakdown: null, history: [] };
  }
};
